import isEqual from 'lodash/isEqual';
import { z } from 'zod';

import {
  zodPlaceholder,
  zodSelectOption,
  zodStringOptional,
  zodStringOptionalWithMinMax,
  zodStringRequiredWithMinMax,
} from '@/shared/lib/validations';

import { FormConstants, IdConstants } from '../../../../components/Forms/constants';
import { CUSTOM_OPTION_ID } from '../../Select';

export const countrySchema = z.object({
  country: zodSelectOption,
});
export const ukrainianAddressBlockBaseSchema = z.object({
  region: zodPlaceholder,
  district: zodPlaceholder,
  settlement: zodPlaceholder,
  settlement_custom: zodPlaceholder,
  street_custom: zodPlaceholder,
  street: zodPlaceholder,
  custom_street: zodPlaceholder,
  house: zodPlaceholder,
  building: zodPlaceholder,
  apartment: zodPlaceholder,
  zip_code: zodPlaceholder,
});
export const internationalAddressBlockBaseSchema = z.object({
  region: z.null().optional(),
  district: z.null().optional(),
  settlement: zodStringRequiredWithMinMax(),
  street: zodStringRequiredWithMinMax(),
  custom_street: z.any(),
  house: zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_7 }),
  building: zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_5 }),
  apartment: zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_5 }),
  zip_code: zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_50 }),
});

export const ukrainianAddressBlockSchema = ukrainianAddressBlockBaseSchema.superRefine((data, ctx) => {
  const isSettlementCustom = data.settlement?.id === 'custom';
  const isStreetCustom = data.street?.id === 'custom';

  for (const key in data) {
    const fieldName = key as keyof typeof data;
    const schema = (() => {
      switch (fieldName) {
        case 'region':
          return zodSelectOption;

        case 'district': {
          if (data.region?.id !== IdConstants.KYIV && data.region?.id !== IdConstants.SEVASTOPOL)
            return zodSelectOption;

          return z.null({ message: 'Для міста Київ та Севастополь дане поле повинно бути порожнім' });
        }

        case 'settlement': {
          if (data.region?.id !== IdConstants.KYIV && data.region?.id !== IdConstants.SEVASTOPOL)
            return zodSelectOption;

          return zodSelectOption.refine(
            (settlement) => isEqual(settlement, data.region),
            'Дане поле має бути таким самим як поле "Область"',
          );
        }

        case 'settlement_custom':
          return isSettlementCustom
            ? zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_100 })
            : z.any().nullish();

        case 'street':
          return isSettlementCustom
            ? z.any().nullable()
            : zodSelectOption.extend({
                postCode: zodStringOptional.nullable(),
              });

        case 'street_custom':
          return isSettlementCustom || isStreetCustom
            ? zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_100 })
            : z.any().nullish();

        case 'custom_street':
          return data.street?.id === CUSTOM_OPTION_ID ? zodStringRequiredWithMinMax() : z.any();

        case 'house':
          return zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_7 });

        case 'building':
          return zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_5 });

        case 'apartment':
          return zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_5 });

        case 'zip_code':
          return zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_50 });

        default:
          throw new Error('Missing validation schema for:', fieldName);
      }
    })();
    const { success, error } = schema.safeParse(data[fieldName]);

    if (!success) {
      for (const issue of error.issues) {
        ctx.addIssue({ ...issue, path: [fieldName] });
      }
    }
  }
});
export const internationalAddressBlockSchema = internationalAddressBlockBaseSchema;

export const ukrainianAddressSchema = z.intersection(countrySchema, ukrainianAddressBlockSchema);
export const internationalAddressSchema = z.intersection(countrySchema, internationalAddressBlockSchema);
export const addressBlockSchema = z.intersection(
  z.union([ukrainianAddressSchema, internationalAddressSchema]),
  z.object({
    settlement_custom: zodStringOptional.nullable().optional(),
    street_custom: zodStringOptional.nullable().optional(),
  }),
);
